import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Package, Server, TrendingUp, Clock, Plus, LogOut, User } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'

interface App {
  app_id: string
  name: string
  description?: string
  status: string
  created_at: string
  updated_at: string
}

export default function AppsList() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [apps, setApps] = useState<App[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [viewingSecret, setViewingSecret] = useState<{ appId: string, appName: string } | null>(null)
  const [authSecret, setAuthSecret] = useState<string>('')
  const [secretLoading, setSecretLoading] = useState(false)
  const [secretError, setSecretError] = useState('')
  const [secretCopied, setSecretCopied] = useState(false)

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  useEffect(() => {
    loadApps()
  }, [])

  const loadApps = async () => {
    try {
      const response = await fetch('/api/admin/apps')
      const data = await response.json()
      setApps(data.apps || [])
    } catch (error) {
      console.error('加载应用列表失败:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleViewSecret = async (appId: string, appName: string) => {
    setViewingSecret({ appId, appName })
    setSecretLoading(true)
    setSecretError('')
    setSecretCopied(false)
    
    try {
      const response = await fetch(`/api/admin/apps/${appId}/auth-secret`)
      const data = await response.json()
      
      if (data.success && data.auth_secret) {
        setAuthSecret(data.auth_secret)
      } else {
        setSecretError(data.message || '获取失败')
      }
    } catch (error) {
      console.error('获取 Auth Secret 失败:', error)
      setSecretError('网络错误，请稍后重试')
    } finally {
      setSecretLoading(false)
    }
  }

  const handleCopySecret = () => {
    navigator.clipboard.writeText(authSecret)
    setSecretCopied(true)
    setTimeout(() => setSecretCopied(false), 2000)
  }

  const handleCloseSecret = () => {
    setViewingSecret(null)
    setAuthSecret('')
    setSecretError('')
    setSecretCopied(false)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-green-400 bg-green-400/10'
      case 'deleted': return 'text-gray-400 bg-gray-400/10'
      default: return 'text-gray-400'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return '运行中'
      case 'deleted': return '已删除'
      default: return status
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-400">加载中...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-dark-bg">
      {/* 顶部导航栏 */}
      <header className="h-16 bg-dark-card border-b border-dark-border flex items-center justify-between px-6">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-bold text-white">OTA 管理系统</h1>
          <span className="text-sm text-gray-400">v0.4.0</span>
        </div>
        <div className="flex items-center gap-4">
          {/* 用户信息 */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center">
                <User size={16} className="text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">{user?.username}</p>
                <p className="text-xs text-gray-400 capitalize">{user?.role}</p>
              </div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-400 hover:text-white hover:bg-dark-border rounded-lg transition-colors"
          >
            <LogOut size={16} />
            退出登录
          </button>
          <span className="text-sm text-gray-400 border-l border-dark-border pl-4">
            {new Date().toLocaleDateString('zh-CN')}
          </span>
        </div>
      </header>

      {/* 主内容区 */}
      <main className="p-6">
        <div className="space-y-6">
          {/* 页面标题 */}
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-white">应用管理</h2>
              <p className="text-gray-400 mt-1">管理你的产品和固件更新项目</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary flex items-center gap-2"
            >
              <Plus size={18} />
              创建应用
            </button>
          </div>

      {/* 应用列表 */}
      {apps.length === 0 ? (
        <div className="card text-center py-12">
          <div className="text-6xl mb-4">📦</div>
          <h3 className="text-xl font-bold text-white mb-2">暂无应用</h3>
          <p className="text-gray-400 mb-6">创建你的第一个应用来开始管理固件更新</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus size={18} />
            创建应用
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {apps.map((app) => (
            <Link
              key={app.app_id}
              to={`/apps/${app.app_id}`}
              className="card hover:border-primary-500/50 transition-colors group"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-lg bg-primary-600/20 flex items-center justify-center">
                  <Package className="text-primary-400" size={24} />
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(app.status)}`}>
                  {getStatusText(app.status)}
                </span>
              </div>

              <h3 className="text-lg font-bold text-white mb-2 group-hover:text-primary-400 transition-colors">
                {app.name}
              </h3>
              
              <p className="text-sm text-gray-400 mb-4 line-clamp-2">
                {app.description || '暂无描述'}
              </p>

              <div className="flex items-center justify-between text-xs text-gray-500">
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-1">
                    <Server size={12} />
                    设备管理
                  </span>
                  <span className="flex items-center gap-1">
                    <TrendingUp size={12} />
                    版本追踪
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      handleViewSecret(app.app_id, app.name)
                    }}
                    className="text-primary-400 hover:text-primary-300 transition-colors"
                    title="查看 Auth Secret"
                  >
                    🔑
                  </button>
                  <Clock size={12} />
                  {new Date(app.created_at).toLocaleDateString('zh-CN')}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* 创建应用弹窗 */}
      {showCreateModal && (
        <CreateAppModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={(newApp) => {
            setApps([...apps, newApp])
            setShowCreateModal(false)
          }}
        />
      )}

      {/* Auth Secret 查看弹窗 */}
      {viewingSecret && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="card w-full max-w-lg">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-white">查看 Auth Secret</h2>
                <p className="text-sm text-gray-400 mt-1">{viewingSecret.appName} ({viewingSecret.appId})</p>
              </div>
              <button
                onClick={handleCloseSecret}
                className="text-gray-400 hover:text-white transition-colors"
              >
                ✕
              </button>
            </div>

            {secretLoading ? (
              <div className="text-center py-8">
                <div className="text-gray-400">加载中...</div>
              </div>
            ) : secretError ? (
              <div className="bg-red-500/10 border border-red-500 text-red-400 px-4 py-3 rounded-lg">
                {secretError}
              </div>
            ) : (
              <>
                <div className="bg-yellow-500/10 border border-yellow-500 text-yellow-400 px-4 py-3 rounded-lg mb-4">
                  <p className="font-bold mb-1">⚠️ 重要提示</p>
                  <p className="text-sm">
                    Auth Secret 用于设备端认证，请妥善保管，不要泄露给他人。
                    建议立即复制到安全的地方保存。
                  </p>
                </div>

                <div className="bg-dark-border rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-gray-400">Auth Secret:</p>
                    <button
                      onClick={handleCopySecret}
                      className="text-xs text-primary-400 hover:text-primary-300 transition-colors flex items-center gap-1"
                    >
                      {secretCopied ? (
                        <>
                          <span>✅ 已复制</span>
                        </>
                      ) : (
                        <>
                          <span>📋 复制</span>
                        </>
                      )}
                    </button>
                  </div>
                  <code className="text-primary-400 text-sm break-all block bg-black/30 p-3 rounded">
                    {authSecret}
                  </code>
                </div>

                <div className="bg-blue-500/10 border border-blue-500 text-blue-400 px-4 py-3 rounded-lg mb-4">
                  <p className="font-bold mb-1">💡 使用示例</p>
                  <p className="text-xs text-gray-300">
                    设备端调用 API 时需携带此密钥：
                  </p>
                  <pre className="text-xs bg-black/30 p-2 rounded mt-2 overflow-x-auto">
{`POST /api/ota/check_update
X-Auth-Secret: ${authSecret}`}
                  </pre>
                </div>
              </>
            )}

            <div className="flex gap-3 pt-4">
              <button
                onClick={handleCopySecret}
                disabled={!authSecret}
                className="btn-secondary flex-1 flex items-center justify-center gap-2"
              >
                📋 {secretCopied ? '已复制' : '复制'}
              </button>
              <button
                onClick={handleCloseSecret}
                className="btn-primary flex-1"
              >
                完成
              </button>
            </div>
          </div>
        </div>
      )}
        </div>
      </main>
    </div>
  )
}

function CreateAppModal({ onClose, onSuccess }: {
  onClose: () => void
  onSuccess: (app: App) => void
}) {
  const [name, setName] = useState('')
  const [appId, setAppId] = useState('')
  const [description, setDescription] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [authSecret, setAuthSecret] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const response = await fetch('/api/admin/apps', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          app_id: appId,
          name,
          description
        })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || '创建失败')
      }

      // 如果创建成功且有 auth_secret，显示给用户
      if (data.auth_secret) {
        setAuthSecret(data.auth_secret)
      } else {
        onSuccess(data)
      }
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  if (authSecret) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="card w-full max-w-lg">
          <h2 className="text-xl font-bold text-white mb-4">✅ 应用创建成功</h2>
          
          <div className="bg-yellow-500/10 border border-yellow-500 text-yellow-400 px-4 py-3 rounded-lg mb-4">
            <p className="font-bold mb-1">⚠️ 重要：请立即下载保存 Auth Secret</p>
            <p className="text-sm">此密钥仅显示一次，关闭后无法再次查看！</p>
          </div>

          <div className="bg-dark-border rounded-lg p-4 mb-4">
            <p className="text-sm text-gray-400 mb-2">Auth Secret:</p>
            <code className="text-primary-400 text-sm break-all">{authSecret}</code>
          </div>

          <div className="bg-dark-border rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-400 mb-2">应用 ID:</p>
            <code className="text-white text-sm">{appId}</code>
          </div>

          <button
            onClick={() => onSuccess({ app_id: appId, name, description, status: 'active', created_at: new Date().toISOString(), updated_at: new Date().toISOString() })}
            className="btn-primary w-full"
          >
            我已保存，完成
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="card w-full max-w-lg">
        <h2 className="text-xl font-bold text-white mb-4">创建新应用</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-2">应用名称 *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="例如：智能时钟"
              className="input w-full"
              required
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">应用 ID *</label>
            <input
              type="text"
              value={appId}
              onChange={(e) => setAppId(e.target.value)}
              placeholder="例如：smart_clock"
              className="input w-full"
              pattern="[a-z0-9_]+"
              title="只能使用小写字母、数字和下划线"
              required
            />
            <p className="text-xs text-gray-500 mt-1">只能使用小写字母、数字和下划线，创建后不可修改</p>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">描述</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="应用描述信息"
              className="input w-full"
              rows={3}
            />
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500 text-red-400 px-4 py-2 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary flex-1"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary flex-1"
            >
              {loading ? '创建中...' : '创建'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
